import React, { useState, useRef, useEffect } from 'react';
import { 
  Container, 
  Card, 
  Button, 
  ProgressBar, 
  Alert, 
  Form, 
  Row, 
  Col,
  Spinner,
  Badge
} from 'react-bootstrap';
import { 
  initFFmpeg, 
  isFFmpegLoaded as checkFFmpegLoaded, 
  setProgressCallback, 
  setStatusCallback,
  convertVideo, 
  cancelConversion, 
  reinitializeFFmpeg 
} from '../services/ffmpegService';

const VideoConverter = () => {
  const [ffmpegReady, setFfmpegReady] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [videoMetadata, setVideoMetadata] = useState(null);
  const [outputFormat, setOutputFormat] = useState('mp4');
  const [qualityOptions, setQualityOptions] = useState([
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' }
  ]);
  const [quality, setQuality] = useState('high');
  const [resolution, setResolution] = useState('original');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [convertedVideo, setConvertedVideo] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [removeAudio, setRemoveAudio] = useState(false);
  const [conversionStartTime, setConversionStartTime] = useState(null);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(null);
  
  const fileInputRef = useRef(null);
  const convertedVideoUrlRef = useRef(null);

  // Initialize FFmpeg
  useEffect(() => {
    const loadFFmpeg = async () => {
      try {
        setStatus('Loading video processor...');
        
        // Set progress callback for FFmpeg operations
        setProgressCallback((progressValue) => {
          setProgress(progressValue);
          
          // Calculate estimated time remaining
          if (conversionStartTime && progressValue > 0 && progressValue < 100) {
            const elapsed = Date.now() - conversionStartTime;
            const estimated = (elapsed / progressValue) * (100 - progressValue);
            setEstimatedTimeRemaining(Math.round(estimated / 1000));
          }
        });
        
        // Set status callback for detailed status messages
        setStatusCallback((statusMessage) => {
          setStatus(statusMessage);
        });
        
        // Initialize FFmpeg
        await initFFmpeg();
        
        setFfmpegReady(true);
        setStatus('Video processor loaded successfully!');
        setTimeout(() => setStatus(''), 3000);
        
      } catch (err) {
        setError('Failed to load video processor. Please refresh the page and try again.');
        console.error('FFmpeg loading error:', err);
      }
    };

    loadFFmpeg();
    
    // Cleanup function to revoke object URLs
    return () => {
      if (convertedVideoUrlRef.current) {
        URL.revokeObjectURL(convertedVideoUrlRef.current);
      }
    };
  }, [conversionStartTime]);

  // Extract comprehensive video metadata using FFmpeg.wasm
  const extractVideoMetadata = async (file) => {
    try {
      // First try basic HTML5 video element for quick basic info
      const basicMetadata = await new Promise((resolve) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(video.src);
          const isVertical = video.videoHeight > video.videoWidth;
          
          resolve({
            width: video.videoWidth,
            height: video.videoHeight,
            duration: video.duration,
            isVertical,
            aspectRatio: (video.videoWidth / video.videoHeight).toFixed(2)
          });
        };
        
        video.onerror = () => {
          URL.revokeObjectURL(video.src);
          resolve(null);
        };
        
        video.src = URL.createObjectURL(file);
      });

      // Get basic file metadata (FFmpeg metadata extraction removed - not accessible from component)
      const detailedMetadata = {
        fileType: file.name.split('.').pop().toUpperCase(),
        fileSize: file.size,
        creationTime: file.lastModified ? new Date(file.lastModified).toLocaleDateString() : 'Unknown'
      };

      // Enhanced metadata extraction using MediaInfo-style approach
      const enhancedMetadata = await extractEnhancedMetadata(file, basicMetadata);
      
      return {
        ...basicMetadata,
        ...detailedMetadata,
        ...enhancedMetadata
      };
    } catch (error) {
      console.error('Error extracting video metadata:', error);
      return null;
    }
  };

  // Extract enhanced metadata using FFmpeg.wasm ffprobe
  const extractEnhancedMetadata = async (file, basicMetadata) => {
    const metadata = {};
    
    try {
      // File type from extension and MIME type
      const extension = file.name.split('.').pop().toUpperCase();
      const mimeType = file.type;
      
      // Map common video formats to codec names
      const formatMapping = {
        'MP4': mimeType.includes('hvc1') || mimeType.includes('hev1') ? 'H.265/HEVC' : 'H.264',
        'MOV': 'QuickTime',
        'AVI': 'AVI',
        'MKV': 'Matroska',
        'WEBM': 'WebM (VP8/VP9)',
        'FLV': 'Flash Video',
        'WMV': 'Windows Media Video',
        'M4V': 'MPEG-4 Video'
      };
      
      metadata.fileType = extension;
      metadata.containerFormat = formatMapping[extension] || extension;
      metadata.fileSize = file.size;
      metadata.creationTime = file.lastModified ? new Date(file.lastModified).toLocaleDateString() : 'Unknown';
      
      // Calculate estimated data rate from file size and duration
      if (basicMetadata && basicMetadata.duration && basicMetadata.duration > 0) {
        const bitrateKbps = (file.size * 8) / (basicMetadata.duration * 1000);
        metadata.dataRate = (bitrateKbps / 1000).toFixed(2) + ' Mbps';
      }
      
      return metadata;
    } catch (error) {
      console.error('Enhanced metadata extraction failed:', error);
      return metadata;
    }
  };
  
  
  // Handle file selection
  const handleFileSelect = async (file) => {
    if (file && file.type.startsWith('video/')) {
      // Check file size limit (1GB = 1024^3 bytes)
      const maxFileSize = 1024 * 1024 * 1024; // 1GB
      const isLargeFile = file.size > maxFileSize;
      
      setSelectedFile(file);
      setConvertedVideo(null);
      
      // Set error message for large files but continue to show metadata
      if (isLargeFile) {
        setError(`File is too large (${formatFileSize(file.size)}). Maximum supported file size is 1GB. For larger files, try free desktop software like HandBrake, FFmpeg desktop, or VLC Media Player. File information is shown below.`);
      } else {
        setError('');
      }
      
      try {
        const metadata = await extractVideoMetadata(file);
        setVideoMetadata(metadata);
        
        // If vertical video detected, suggest vertical resolution
        if (metadata && metadata.isVertical) {
          // Keep resolution as 'original' but we'll use this metadata later
          console.log('Vertical video detected:', metadata);
        }
      } catch (err) {
        console.error('Error extracting video metadata:', err);
        // Continue without metadata
        setVideoMetadata(null);
      }
    } else {
      setError('Please select a valid video file.');
    }
  };

  // Clear selected files and reset state
  const clearFiles = () => {
    setSelectedFile(null);
    setVideoMetadata(null);
    setConvertedVideo(null);
    setProgress(0);
    setStatus('');
    setError('');
    setIsCancelled(false);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Stop conversion process
  const stopConversion = async () => {
    try {
      // Signal FFmpeg service to cancel
      cancelConversion();
      
      // Update UI state
      setIsCancelled(true);
      setIsProcessing(false);
      setStatus('Stopping conversion...');
      
      // Give FFmpeg a moment to cancel gracefully
      setTimeout(async () => {
        try {
          // If FFmpeg is still stuck, force reinitialize
          if (isProcessing) {
            setStatus('Force stopping FFmpeg...');
            await reinitializeFFmpeg();
          }
          setStatus('Conversion cancelled by user');
          setProgress(0);
        } catch (error) {
          console.error('Error during force stop:', error);
          setStatus('Conversion stopped (FFmpeg may need refresh)');
          setError('If you encounter issues, please refresh the page');
        }
      }, 2000); // Wait 2 seconds for graceful cancellation
      
    } catch (error) {
      console.error('Error stopping conversion:', error);
      setStatus('Error stopping conversion');
      setError('Please refresh the page if conversion issues persist');
    }
  };

  // Update quality options based on output format
  const updateQualityOptions = (format) => {
    switch (format) {
      case 'hevc':
        // HEVC quality options
        setQualityOptions([
          { value: 'low', label: 'Low (28 CRF)' },
          { value: 'medium', label: 'Medium (23 CRF)' },
          { value: 'high', label: 'High (18 CRF)' }
        ]);
        break;
      case 'webm':
        // WebM (VP9) quality options
        setQualityOptions([
          { value: 'low', label: 'Low (35 CRF)' },
          { value: 'medium', label: 'Medium (31 CRF)' },
          { value: 'high', label: 'High (24 CRF)' }
        ]);
        break;
      case 'mp3':
        // Audio has bitrate-focused quality options
        setQualityOptions([
          { value: 'low', label: '128 kbps' },
          { value: 'medium', label: '192 kbps' },
          { value: 'high', label: '320 kbps' }
        ]);
        break;
      default:
        // Default options for MP4 and AVI
        setQualityOptions([
          { value: 'low', label: 'Low (28 CRF)' },
          { value: 'medium', label: 'Medium (23 CRF)' },
          { value: 'high', label: 'High (18 CRF)' }
        ]);
        break;
    }
  };
  
  // Handle output format change
  const handleOutputFormatChange = (e) => {
    const newFormat = e.target.value;
    setOutputFormat(newFormat);
    updateQualityOptions(newFormat);
  };
  
  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Process video conversion
  const processVideo = async () => {
    if (!selectedFile || !ffmpegReady) {
      setError('Please select a file and wait for the processor to load.');
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setError('');
    setConvertedVideo(null);
    setStatus('Processing video...');
    setIsCancelled(false); // Reset cancellation flag
    setConversionStartTime(Date.now()); // Track start time for ETA
    setEstimatedTimeRemaining(null);

    try {
      // Get conversion settings
      const conversionSettings = {
        outputFormat,
        quality,
        resolution,
        removeAudio
      };
      
      // Check if conversion was cancelled before starting
      if (isCancelled) {
        setStatus('Conversion cancelled');
        return;
      }
      
      // Convert video using FFmpeg service
      const result = await convertVideo(selectedFile, conversionSettings);
      
      // Check if conversion was cancelled after processing
      if (isCancelled) {
        setStatus('Conversion cancelled');
        return;
      }
      
      // Create download URL
      const outputBlob = new Blob([result.data], { 
        type: result.mimeType 
      });
      const url = URL.createObjectURL(outputBlob);
      
      setConvertedVideo({
        url,
        filename: `converted_${selectedFile.name.split('.')[0]}.${result.extension}`,
        size: outputBlob.size
      });

      setStatus('Conversion completed successfully!');
      setProgress(100);
      
    } catch (err) {
      setError(`Conversion failed: ${err.message}`);
      console.error('Conversion error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={10}>
          <Card className="mb-4">
            <Card.Header style={{
              background: 'linear-gradient(135deg, rgba(91, 141, 239, 0.12) 0%, rgba(167, 139, 250, 0.08) 100%)',
              borderBottom: '1px solid rgba(91, 141, 239, 0.15)',
              padding: '0.75rem 1.25rem'
            }}>
              <h3 className="mb-0" style={{ color: '#e2e8f0' }}>
                Converter Status
                {ffmpegReady && <Badge bg="success" className="ms-2">Ready!</Badge>}
                {!ffmpegReady && <Badge bg="warning" className="ms-2">Loading...</Badge>}
              </h3>
            </Card.Header>
            
            <Card.Body>
              {/* Status and Error Messages */}
              {status && !error && (
                <Alert variant="info" className="d-flex align-items-center">
                  {isProcessing && <Spinner animation="border" size="sm" className="me-2" />}
                  {status}
                </Alert>
              )}
              
              {error && (
                <Alert variant="danger">
                  {error}
                </Alert>
              )}

              {/* File Upload Area */}
              <Card className="mb-4">
                <Card.Body
                  className={`text-center p-4 ${isDragOver ? 'bg-light border-primary' : ''} drag-area ${isDragOver ? 'active' : ''}`}
                  style={{ 
                    cursor: 'pointer'
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="mb-3">
                    <i className="fas fa-cloud-upload-alt fa-3x text-muted"></i>
                  </div>
                  <h5>Drag & Drop Video File Here</h5>
                  <p className="text-muted">or click to browse</p>
                  
                  {selectedFile && (
                    <div className="mt-3">
                      <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                        <Badge bg="primary" className="p-2">
                          📹 {selectedFile.name} ({formatFileSize(selectedFile.size)})
                        </Badge>
                        <Button 
                          variant="outline-danger" 
                          size="sm" 
                          onClick={clearFiles}
                          title="Remove file"
                        >
                          ✕
                        </Button>
                      </div>
                      
                      {videoMetadata && (
                        <div className="mt-3">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="text-start">
                                <h6 className="text-primary mb-2">📊 Video Information</h6>
                                <small className="d-block text-muted mb-1">
                                  <strong>File Type:</strong> {videoMetadata.fileType || 'Unknown'}
                                </small>
                                <small className="d-block text-muted mb-1">
                                  <strong>Container:</strong> {videoMetadata.containerFormat || 'Unknown'}
                                </small>
                                <small className="d-block text-muted mb-1">
                                  <strong>Resolution:</strong> {videoMetadata.width}×{videoMetadata.height} 
                                  ({videoMetadata.isVertical ? 'Vertical' : 'Horizontal'})
                                </small>
                                <small className="d-block text-muted mb-1">
                                  <strong>Duration:</strong> {Math.floor(videoMetadata.duration / 60)}:{Math.floor(videoMetadata.duration % 60).toString().padStart(2, '0')}
                                </small>
                                <small className="d-block text-muted mb-1">
                                  <strong>Aspect Ratio:</strong> {videoMetadata.aspectRatio}
                                </small>
                                <small className="d-block text-muted mb-1">
                                  <strong>Data Rate:</strong> {videoMetadata.dataRate || 'Calculating...'}
                                </small>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="text-start">
                                <h6 className="text-info mb-2">📁 File Information</h6>
                                <small className="d-block text-muted mb-1">
                                  <strong>Creation Date:</strong> {videoMetadata.creationTime || 'Unknown'}
                                </small>
                                <small className="d-block text-muted mb-1">
                                  <strong>File Size:</strong> {formatFileSize(videoMetadata.fileSize || selectedFile.size)}
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect(e.target.files[0])}
                  />
                </Card.Body>
              </Card>

              {/* Conversion Settings */}
              <Card className="mb-4">
                <Card.Header style={{
                  background: 'linear-gradient(135deg, rgba(91, 141, 239, 0.12) 0%, rgba(167, 139, 250, 0.08) 100%)',
                  borderBottom: '1px solid rgba(91, 141, 239, 0.15)',
                  padding: '0.75rem 1.25rem'
                }}>
                  <h5 className="mb-0" style={{ color: '#e2e8f0' }}>Conversion Settings</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Format</Form.Label>
                        <Form.Select 
                          value={outputFormat} 
                          onChange={handleOutputFormatChange}
                        >
                          <option value="mp4">MP4 (H.264)</option>
                          <option value="webm">WebM (VP9)</option>
                          <option value="avi">AVI</option>
                          <option value="mp3">MP3 (Audio Only)</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Quality</Form.Label>
                        <Form.Select 
                          value={quality} 
                          onChange={(e) => setQuality(e.target.value)}
                        >
                          {qualityOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Resolution</Form.Label>
                        <Form.Select 
                          value={resolution} 
                          onChange={(e) => setResolution(e.target.value)}
                          disabled={outputFormat === 'mp3'}
                        >
                          {outputFormat === 'mp3' ? (
                            <option value="not-applicable">Not applicable for audio only</option>
                          ) : (
                            <>
                              <option value="original">Original</option>
                              {videoMetadata && videoMetadata.isVertical ? (
                                /* Vertical video options */
                                <>
                                  <option value="1080:1920">1080p Vertical (1080×1920)</option>
                                  <option value="720:1280">720p Vertical (720×1280)</option>
                                  <option value="480:854">480p Vertical (480×854)</option>
                                </>
                              ) : (
                                /* Horizontal video options */
                                <>
                                  <option value="1920:1080">1080p (1920×1080)</option>
                                  <option value="1280:720">720p (1280×720)</option>
                                  <option value="854:480">480p (854×480)</option>
                                </>
                              )}
                            </>
                          )}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={1} className="d-flex align-items-center">
                      <Form.Group className="mb-3">
                        <Form.Label className="d-block small">Audio</Form.Label>
                        <Form.Check
                          type="switch"
                          id="remove-audio-switch"
                          label="Mute"
                          checked={removeAudio}
                          onChange={(e) => setRemoveAudio(e.target.checked)}
                          disabled={outputFormat === 'mp3'}
                          title="Remove audio track from output"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Progress Bar */}
              {(isProcessing || progress > 0) && (
                <Card className="mb-4 progress-card">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-semibold">
                        {progress === 100 ? '✅ Complete' : '🔄 Conversion Progress'}
                      </span>
                      <div className="text-end">
                        <span className="fw-bold">{progress}%</span>
                        {isProcessing && estimatedTimeRemaining && progress > 0 && progress < 100 && (
                          <small className="d-block text-muted">
                            ~{estimatedTimeRemaining > 60 
                              ? `${Math.floor(estimatedTimeRemaining / 60)}m ${estimatedTimeRemaining % 60}s` 
                              : `${estimatedTimeRemaining}s`} remaining
                          </small>
                        )}
                      </div>
                    </div>
                    <ProgressBar 
                      now={progress} 
                      variant={progress === 100 ? "success" : "primary"}
                      animated={isProcessing}
                      striped={isProcessing}
                      style={{ height: '12px' }}
                    />
                    {isProcessing && status && (
                      <small className="d-block text-muted mt-2 text-center">
                        {status}
                      </small>
                    )}
                  </Card.Body>
                </Card>
              )}

              {/* Convert/Stop Button */}
              <div className="text-center mb-4">
                {isProcessing ? (
                  <div className="d-flex justify-content-center gap-3">
                    <Button
                      variant="primary"
                      size="lg"
                      disabled
                      className="px-5"
                    >
                      <Spinner animation="border" size="sm" className="me-2" />
                      Converting...
                    </Button>
                    <Button
                      variant="danger"
                      size="lg"
                      onClick={stopConversion}
                      className="px-4"
                    >
                      ⏹️ Stop
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={processVideo}
                    disabled={!selectedFile || !ffmpegReady}
                    className="px-5"
                  >
                    Convert Video
                  </Button>
                )}
              </div>

              {/* Download Link */}
              {convertedVideo && (
                <Card className="border-success">
                  <Card.Header className="bg-success text-white">
                    <h5 className="mb-0">✅ Conversion Complete!</h5>
                  </Card.Header>
                  <Card.Body className="text-center">
                    <div className="mb-3">
                      <Badge bg="success" className="p-2">
                        📁 {convertedVideo.filename} ({formatFileSize(convertedVideo.size)})
                      </Badge>
                    </div>
                    <Button
                      variant="success"
                      size="lg"
                      href={convertedVideo.url}
                      download={convertedVideo.filename}
                      className="px-4"
                    >
                      📥 Download Converted Video
                    </Button>
                  </Card.Body>
                </Card>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default VideoConverter;
